import { CurrencyType } from "../../utils/currency";
import { PlanTier } from "../../utils/permissions";
import type { PartnerPaidBy } from "../Plans";

export type PaidTier =
  | PlanTier.PREMIUM
  | PlanTier.ENTERPRISE
  | PlanTier.LARGE_BUSINESS;

export type BillingInterval = "month" | "annual";

interface PlanInfo {
  tier: PaidTier;
  interval: BillingInterval;
  currency: CurrencyType;
}

const P = PlanTier.PREMIUM;
const E = PlanTier.ENTERPRISE;
const L = PlanTier.LARGE_BUSINESS;

const plan = (
  tier: PaidTier,
  interval: BillingInterval,
  currency: CurrencyType
): PlanInfo => ({ tier, interval, currency });

/** Recurring Stripe plans a customer pays itself, legacy codes included. */
const CATALOG: Record<string, PlanInfo> = {
  UMD_PREMIUM_BUSINESS_MONTH: plan(P, "month", "USD"),
  UMD_PREMIUM_BUSINESS_ANNUAL: plan(P, "annual", "USD"),
  UMD_PREMIUM_BUSINESS_ANNUAL_WITH_TRIAL: plan(P, "annual", "USD"),
  SUMOM_8PS8HPOLTF: plan(P, "month", "EUR"),
  SUMOA_EUEIVLUAUW: plan(P, "annual", "EUR"),
  SUMOA_T9IZI415ZG: plan(P, "annual", "EUR"),
  SUMOM_MHMYDHE9NH: plan(P, "month", "CHF"),
  SUMOA_HOYZZNJVYT: plan(P, "annual", "CHF"),
  SUMOA_9X8ZNA5QAO: plan(P, "annual", "CHF"),
  SUMOM_USXDYFUDSY: plan(P, "month", "CHF"),
  SUMOA_HVDYRRYCHY: plan(P, "annual", "CHF"),
  UMD_ENTREPRISE_BUSINESS_MONTH: plan(E, "month", "USD"),
  UMD_ENTREPRISE_BUSINESS_ANNUAL: plan(E, "annual", "USD"),
  UMD_ENTREPRISE_BUSINESS_ANNUAL_WITH_TRIAL: plan(E, "annual", "USD"),
  SUMOM_GYASCZ0RSW: plan(E, "month", "EUR"),
  SUMOA_VJTRNHTBIB: plan(E, "annual", "EUR"),
  SUMOA_YNL4PD6BOM: plan(E, "annual", "EUR"),
  SUMOM_JVXKQNIFEN: plan(E, "month", "CHF"),
  SUMOA_BOAXCZVLIQ: plan(E, "annual", "CHF"),
  SUMOA_GSW9Q445J1: plan(E, "annual", "CHF"),
  SUMOM_PAISDXBDUS: plan(E, "month", "CHF"),
  SUMOA_OGIDHUNOFG: plan(E, "annual", "CHF"),
  UMD_LARGE_BUSINESS_MONTH: plan(L, "month", "USD"),
  UMD_LARGE_BUSINESS_ANNUAL: plan(L, "annual", "USD"),
  UMD_LARGE_BUSINESS_ANNUAL_WITH_TRIAL: plan(L, "annual", "USD"),
  SUMOM_A8NQTU2UIB: plan(L, "month", "EUR"),
  SUMOA_HZVGJGKYLM: plan(L, "annual", "EUR"),
  SUMOA_7AWE1A948A: plan(L, "annual", "EUR"),
  SUMOM_7YV3KX83JA: plan(L, "month", "CHF"),
  SUMOA_ZEDIVSBYFC: plan(L, "annual", "CHF"),
  SUMOA_ZN8D246RA5: plan(L, "annual", "CHF"),
};

/** The codes the plan grid sells today, the same table as `Plans.tsx`. */
const SOLD: Record<
  PaidTier,
  Record<BillingInterval, Record<CurrencyType, string>>
> = {
  [P]: {
    month: {
      USD: "UMD_PREMIUM_BUSINESS_MONTH",
      EUR: "SUMOM_8PS8HPOLTF",
      CHF: "SUMOM_MHMYDHE9NH",
    },
    annual: {
      USD: "UMD_PREMIUM_BUSINESS_ANNUAL",
      EUR: "SUMOA_EUEIVLUAUW",
      CHF: "SUMOA_HOYZZNJVYT",
    },
  },
  [E]: {
    month: {
      USD: "UMD_ENTREPRISE_BUSINESS_MONTH",
      EUR: "SUMOM_GYASCZ0RSW",
      CHF: "SUMOM_JVXKQNIFEN",
    },
    annual: {
      USD: "UMD_ENTREPRISE_BUSINESS_ANNUAL",
      EUR: "SUMOA_VJTRNHTBIB",
      CHF: "SUMOA_BOAXCZVLIQ",
    },
  },
  [L]: {
    month: {
      USD: "UMD_LARGE_BUSINESS_MONTH",
      EUR: "SUMOM_A8NQTU2UIB",
      CHF: "SUMOM_7YV3KX83JA",
    },
    annual: {
      USD: "UMD_LARGE_BUSINESS_ANNUAL",
      EUR: "SUMOA_HZVGJGKYLM",
      CHF: "SUMOA_ZEDIVSBYFC",
    },
  },
};

const TIER_RANK: Record<PaidTier, number> = { [P]: 1, [E]: 2, [L]: 3 };

export const planInfo = (code: string | undefined | null): PlanInfo | null =>
  (code && CATALOG[code]) || null;

export const soldPlanCode = (
  tier: PaidTier,
  interval: BillingInterval,
  currency: CurrencyType
) => SOLD[tier][interval][currency];

export type PlanChangeKind =
  | "CURRENT"
  | "UPGRADE"
  | "PERIOD_END"
  | "QUOTE"
  | "UNAVAILABLE";

/** Mirrors the back end's rule; the preview's `timing` stays the authority. */
export const classifyPlanChange = (
  fromCode: string,
  toCode: string
): PlanChangeKind => {
  const from = planInfo(fromCode);
  const to = planInfo(toCode);
  if (!from || !to) return "UNAVAILABLE";
  if (from.tier === to.tier && from.interval === to.interval) return "CURRENT";
  if (from.tier === L || to.tier === L) return "QUOTE";
  if (from.currency !== to.currency) return "UNAVAILABLE";

  const rankDelta = TIER_RANK[to.tier] - TIER_RANK[from.tier];
  if (rankDelta < 0) return "PERIOD_END";
  if (from.interval === "annual" && to.interval === "month")
    return "PERIOD_END";
  return "UPGRADE";
};

/** A cross change (annual towards a higher tier billed monthly) can go up today on the annual. */
export const immediateAlternative = (
  fromCode: string,
  toCode: string
): string | null => {
  const from = planInfo(fromCode);
  const to = planInfo(toCode);
  if (!from || !to) return null;
  if (
    from.interval === "annual" &&
    to.interval === "month" &&
    TIER_RANK[to.tier] > TIER_RANK[from.tier] &&
    to.tier !== L
  ) {
    return soldPlanCode(to.tier, "annual", from.currency);
  }
  return null;
};

export interface PlanStatusEntry {
  plan: string;
  description?: string;
  status?: "ACTIVE" | "SUCCEEDED" | "CANCELED" | "ENDED" | "UNPAID" | "ERROR";
  paid_by?: PartnerPaidBy | null;
  cancel_at_period_end?: boolean;
  /** UTC without zone, as PayMod gives it. */
  current_period_end?: string | null;
}

/** A recurring plan of the catalog the customer pays itself whose subscription stops at the end of its period. */
export const isEndingAtPeriodEnd = (entry: PlanStatusEntry) =>
  !entry.paid_by &&
  Boolean(planInfo(entry.plan)) &&
  entry.status === "CANCELED" &&
  entry.cancel_at_period_end === true;

/**
 * A recurring plan of the catalog the customer pays itself, running or ending at period end. Its cancellation stops
 * it at the end of the period; any other plan (frozen subscription, plan outside the catalog) is cancelled at once.
 */
export const isSelfPaidPlan = (entry: PlanStatusEntry) =>
  (!entry.paid_by &&
    Boolean(planInfo(entry.plan)) &&
    entry.status === "ACTIVE") ||
  isEndingAtPeriodEnd(entry);

/** The recurring plan the customer pays itself, the highest tier when several still run. */
export const findSelfPaidPlan = (
  planList: PlanStatusEntry[] | undefined | null
): (PlanStatusEntry & { info: PlanInfo }) | null => {
  const candidates = (planList || [])
    .filter(isSelfPaidPlan)
    .map((entry) => ({ ...entry, info: planInfo(entry.plan)! }));
  if (candidates.length === 0) return null;
  return candidates.reduce((best, entry) =>
    TIER_RANK[entry.info.tier] > TIER_RANK[best.info.tier] ? entry : best
  );
};

export const tierNameKey: Record<PaidTier, string> = {
  [P]: "Pricing.Section1.Premium.key1",
  [E]: "Pricing.Section1.Enterprise.key1",
  [L]: "Pricing.Section1.Large businesses.key1",
};
